import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button } from "antd";
import Wrapper from "./style";
import { CloseCircleFilled, PlusCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { customBaseUrl, httpGet, httpPost } from "../../../store/http";
import Swal from "sweetalert2";
import { hideLoader, showLoader } from "../../../utils/loader";
import { Pagination, Stack } from "@mui/material";

const RefferalWebForm = ({ showModal, hideModal, title, className }) => {
  const getInitialTransactionState = useCallback(() => ({
    tranType: "TRANSFER",
    userId: "",
    tranCrncy: "NGN",
    amount: "",
    customerCreditAccount: "",
    officeDebitAccount: "",
    paymentReference: `WAYA${new Date().getTime()}`,
    tranNarration: "",
    walletOp: [],
  }), []);

  const [data, setData] = useState(getInitialTransactionState());
  const [transactions, setTransactions] = useState([getInitialTransactionState()]);
  const [officialWalletList, setOfficialWalletList] = useState([]);
  const [page, setPage] = useState(1);

  const getOfficialWallets = useCallback(async () => {
    const res = await httpGet(`${customBaseUrl.walletUrl}/api/v1/wallet/waya/official/account`);
    if (res?.status) {
      setOfficialWalletList(res.data || []);
    }
  }, []);

  useEffect(() => {
    getOfficialWallets();
  }, [getOfficialWallets]);

  const getWallets = useCallback(async () => {
    if (!data?.userId) return;
    const res = await httpGet(`${customBaseUrl.walletUrl}/api/v1/wallet/account/count/${data.userId}`);
    if (res?.status) {
      setData(prev => ({ ...prev, walletOp: res.data || [] }));
    }
  }, [data?.userId]);

  useEffect(() => {
    getWallets();
  }, [getWallets]);

  const handleChange = (event) => {
    event.preventDefault();
    const hasEmptyFields = Object.values(data).some(val => val === "" && !Array.isArray(val));
    if (hasEmptyFields) {
      Swal.fire("Warning", "Please fill all the fields", "warning");
    } else {
      const newState = getInitialTransactionState();
      setTransactions(prev => [newState, ...prev]);
      setPage(1);
      setData(newState);
    }
  };

  const handlePaginateChange = (event, pageV) => {
    const currentIndex = pageV - 1;
    setPage(pageV);
    setData(transactions[currentIndex]);
  };

  useEffect(() => {
    setTransactions(prev => {
      const updated = [...prev];
      updated[page - 1] = data;
      return updated;
    });
  }, [data, page]);

  const handleRemoveUser = () => {
    if (transactions.length === 1) {
      const reset = getInitialTransactionState();
      setTransactions([reset]);
      setData(reset);
      setPage(1);
    } else {
      const indexToRemove = page - 1;
      const newTransactions = transactions.filter((_, i) => i !== indexToRemove);
      setTransactions(newTransactions);
      const newPage = page > newTransactions.length ? newTransactions.length : page;
      setPage(newPage);
      setData(newTransactions[newPage - 1]);
    }
  };

  const handleTransaction = async () => {
    showLoader();
    const payloadData = transactions.map(({ walletOp, ...rest }) => rest);
    let response = await httpPost(`${customBaseUrl.logUrl}/api/v1/requestlog/create`, {
      payload: JSON.stringify(payloadData),
      requestId: `${new Date().getTime()}`,
      requestType: "multiBonus",
      customerName: 'Multiple Bonus'
    });

    hideLoader();
    if (response.status === true) {
      Swal.fire('Done', response.message, 'success');
      hideModal(false);
    } else {
      Swal.fire('Error', response.message, 'error');
    }
  };

  return (
    <Modal
      className={className}
      centered
      visible={showModal}
      onCancel={() => hideModal(false)}
      width={500}
      footer={null}
      closable={false}
      title={
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <span>{title}</span>
          <CloseCircleFilled
            style={{ cursor: "pointer", fontSize: "22px" }}
            onClick={() => hideModal(false)}
          />
        </div>
      }
      bodyStyle={{ padding: "23px 73px 44px 73px", borderRadius: "12px" }}
    >
      <Wrapper className='row g-3 needs-validation mb-2 p-3'>
        <div className="col-md-12">
          <label className='form-label'>Receiver's Id</label>
          <input
            type='text'
            className='form-control'
            required
            onChange={(e) => setData({ ...data, userId: e.target.value })}
            value={data.userId}
          />
        </div>
        <div className='col-md-12'>
          <label className="form-label">Amount</label>
          <input
            className='form-control'
            value={data.amount}
            onChange={(e) => setData({ ...data, amount: e.target.value })}
          />
        </div>

        <div className='col-md-12'>
          <label className='form-label'>Official wallet</label>
          <select
            onChange={(e) => {
              const val = e.target.value ? JSON.parse(e.target.value) : null;
              setData({ ...data, officeDebitAccount: val?.accountNo || "" });
            }}
            className='border border-dark rounded form-select'
            value={data.officeDebitAccount ? JSON.stringify(officialWalletList?.find(item => item.accountNo === data.officeDebitAccount)) : ""}
          >
            <option value='' disabled>select wallet</option>
            {officialWalletList.map((option) => (
              <option key={option.accountNo} value={JSON.stringify(option)}>
                {`${option.acct_name} - ${option.accountNo}`}
              </option>
            ))}
          </select>
        </div>

        <div className='col-md-12'>
          <label className='form-label'>Sender's wallet</label>
          <select
            onChange={(e) => {
              const val = e.target.value ? JSON.parse(e.target.value) : null;
              setData({ ...data, customerCreditAccount: val?.accountNo || "" });
            }}
            className='border border-dark rounded form-select'
            value={data.customerCreditAccount ? JSON.stringify(data.walletOp?.find(item => item.accountNo === data.customerCreditAccount)) : ""}
          >
            <option value='' disabled>select wallet</option>
            {data?.walletOp?.map((option) => (
              <option key={option.accountNo} value={JSON.stringify(option)}>
                {`${option.acct_name} - ${option.accountNo} - ${option?.description || ""}`}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-12">
          <label className='form-label'>Narration</label>
          <input
            type='text'
            className='form-control'
            required
            onChange={(e) => setData({ ...data, tranNarration: e.target.value })}
            value={data.tranNarration}
          />
        </div>
      </Wrapper>

      <div style={{ marginTop: "10px" }} className="row">
        <div className="col-md-3">
          <Button
            className="iconUI"
            style={{ color: "#C4C4C4", border: "1px solid #C4C4C4" }}
            onClick={handleChange}
            icon={<PlusCircleOutlined />}
          >
            Add
          </Button>
        </div>
        <div className="col-md-3">
          <Button
            className="iconUI"
            onClick={handleRemoveUser}
            style={{ color: "#FF4B01", border: "1px solid #FF4B01" }}
            icon={<CloseOutlined />}
          >
            Remove
          </Button>
        </div>
        <div className="col-md-6">
          <Button
            type="primary"
            onClick={handleTransaction}
            style={{ background: "#FF4B01", border: "none", float: "right" }}
          >
            Proceed
          </Button>
        </div>
      </div>

      <div className="row" style={{ marginTop: "15px" }}>
        <div className="col-md-12">
          <Stack spacing={2} alignItems="center">
            <Pagination
              count={transactions.length}
              page={page}
              onChange={handlePaginateChange}
              color="primary"
            />
          </Stack>
        </div>
      </div>
    </Modal>
  );
};

export default RefferalWebForm;
